import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, ExternalLink, Trash2, Loader2, Newspaper, CalendarClock } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";

const L = {
  fr: {
    title: "Articles de blog IA",
    subtitle: "Un nouvel article SEO est généré automatiquement chaque lundi à 7h. Les prix cités proviennent de vos offres actuelles.",
    generate: "Générer un article",
    generating: "Rédaction en cours… (~30s)",
    generated: "Article publié :",
    genFailed: "Échec de la génération",
    preview: "Voir",
    del: "Supprimer",
    delConfirm: "Supprimer cet article ?",
    deleted: "Article supprimé",
    empty: "Aucun article généré pour le moment. Cliquez sur « Générer un article » pour publier le premier.",
    auto: "Auto · chaque lundi 7h",
  },
  en: {
    title: "AI blog articles",
    subtitle: "A new SEO article is generated automatically every Monday at 7am. Prices quoted come from your current offers.",
    generate: "Generate an article",
    generating: "Writing… (~30s)",
    generated: "Article published:",
    genFailed: "Generation failed",
    preview: "View",
    del: "Delete",
    delConfirm: "Delete this article?",
    deleted: "Article deleted",
    empty: "No generated articles yet. Click \u201cGenerate an article\u201d to publish the first one.",
    auto: "Auto · every Monday 7am",
  },
};

export const BlogPanel = () => {
  const { lang } = useLang();
  const t = L[lang] || L.fr;
  const [articles, setArticles] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/blog/articles").then((r) => setArticles(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const res = await api.post("/blog/generate");
      toast.success(`${t.generated} ${res.data.fr.title}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t.genFailed);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(t.delConfirm)) return;
    await api.delete(`/blog/articles/${slug}`);
    toast.success(t.deleted);
    load();
  };

  return (
    <div className="border border-white/10 bg-[#111111] p-6" data-testid="blog-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Newspaper className="w-4 h-4 text-[#FF7900]" />
            <h3 className="font-display font-semibold">{t.title}</h3>
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 px-2 py-0.5 rounded-full">
              <CalendarClock className="w-3 h-3" /> {t.auto}
            </span>
          </div>
          <p className="text-xs text-white/40 max-w-xl">{t.subtitle}</p>
        </div>
        <button
          onClick={generate}
          disabled={busy}
          data-testid="blog-generate-button"
          className="inline-flex items-center gap-2 bg-[#FF7900] hover:bg-white hover:text-black disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-300"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {busy ? t.generating : t.generate}
        </button>
      </div>
      {articles.length === 0 ? (
        <p className="text-sm text-white/40 py-6 text-center" data-testid="blog-panel-empty">{t.empty}</p>
      ) : (
        <div className="divide-y divide-white/5">
          {articles.map((a) => {
            const c = a[lang] || a.fr;
            return (
              <div key={a.slug} className="py-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`blog-panel-row-${a.slug}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{c.title}</p>
                  <p className="text-xs text-white/35 mt-0.5">
                    <span className="text-[#FF7900]">{c.tag}</span> · {new Date(a.date).toLocaleDateString(lang === "fr" ? "fr-BE" : "en-GB", { day: "numeric", month: "long", year: "numeric" })} · /blog/{a.slug}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <a
                    href={`/blog/${a.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`blog-preview-${a.slug}`}
                    className="inline-flex items-center gap-1.5 border border-white/15 hover:border-[#FF7900] hover:text-[#FF7900] text-xs px-3.5 py-1.5 rounded-full transition-colors duration-300"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> {t.preview}
                  </a>
                  <button
                    onClick={() => remove(a.slug)}
                    data-testid={`blog-delete-${a.slug}`}
                    className="inline-flex items-center gap-1.5 border border-white/15 hover:border-red-500 hover:text-red-400 text-xs px-3.5 py-1.5 rounded-full transition-colors duration-300"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> {t.del}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
